// Owner-cancellation controller for the Task detail surface. The served projection decides whether the
// affordance exists; this hook sends the exact revision/reason and renders only the verified settlement.

import { useState } from 'react';
import type { TaskDetailProjection } from '../surfaces/know-projections.ts';
import { useSurfaceApi } from '../surfaces/SurfaceApiContext.tsx';
import type { TaskCancellationRouteResult } from '../surfaces/task-cancellation-api.ts';
import { notifyTaskProjectionsChanged } from '../surfaces/task-projection-events.ts';

export interface TaskCancellationDecision {
  readonly busy: boolean;
  readonly note: string | null;
  readonly cancel: (reason: string) => Promise<void>;
}

export function useTaskCancellation(
  detail: TaskDetailProjection,
  reload: () => void,
  onActionNotice: (notice: string) => void,
): TaskCancellationDecision {
  const api = useSurfaceApi();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const cancel = async (reason: string): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      const result = await api.cancelTask(detail.id, detail.definitionRevision, reason);
      if (result.ok) {
        onActionNotice(cancellationNotice(result));
        notifyTaskProjectionsChanged();
        reload();
      } else setNote(result.note ?? 'That cancellation was not permitted.');
    } catch {
      setNote('That cancellation could not reach the task. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return { busy, note, cancel };
}

function cancellationNotice(result: Extract<TaskCancellationRouteResult, { ok: true }>): string {
  const runWord = result.interruptedRunCount === 1 ? 'run' : 'runs';
  const triggerWord = result.cancelledQueuedTriggerCount === 1 ? 'trigger' : 'triggers';
  return `Task cancelled. ${result.interruptedRunCount} active Agent ${runWord} stopped; ${result.cancelledQueuedTriggerCount} queued Task ${triggerWord} cancelled. Emails already sent and completed provider actions remain on the record.`;
}
